import React from 'react';
import { observer } from 'mobx-react-lite';
import { X, Clock, User } from 'lucide-react';
import { Service } from '../../model/professional/service-model';
import { Option } from '../../model/professional/option-model';
import { useTranslation } from 'react-i18next';

interface CartItem {
  service: Service;
  options: Option[];
  collaborator: any;
}

interface MultiServiceCartProps {
  items: CartItem[];
  onRemoveItem: (index: number) => void;
}

const MultiServiceCart: React.FC<MultiServiceCartProps> = observer(({ items, onRemoveItem }) => {
  const { t } = useTranslation();

  if (items.length === 0) {
    return null;
  }

  const calculateItemPrice = (item: CartItem) => {
    const servicePrice = typeof item.service.price === 'number' ?
      item.service.price :
      parseFloat(item.service.price as any) || 0;

    const optionsPrice = item.options.reduce((total, option) => {
      const optionPrice = typeof option.price === 'number' ?
        option.price :
        parseFloat(option.price as any) || 0;
      return total + optionPrice;
    }, 0);

    return servicePrice + optionsPrice;
  };

  const calculateItemDuration = (item: CartItem) => {
    const serviceDuration = item.service.duration || 0;
    const optionsDuration = item.options.reduce((total, option) => {
      return total + (option.durationMinutes || 0);
    }, 0);
    return serviceDuration + optionsDuration;
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-900">
          {t('booking.selectedServices', 'Services sélectionnés')} ({items.length})
        </h3>
      </div>

      <div className="space-y-2">
        {items.map((item, index) => (
          <div
            key={index}
            className="relative bg-white rounded-lg p-3 border border-gray-200"
          >
            {/* Bouton supprimer */}
            <button
              onClick={() => onRemoveItem(index)}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
              title={t('booking.removeService', 'Retirer le service') as string}
            >
              <X size={16} />
            </button>

            {/* Nom du service */}
            <div className="pr-8">
              <h4 className="font-semibold text-gray-800 text-sm mb-1">
                {item.service.name}
              </h4>

              {/* Collaborateur */}
              {item.collaborator && (
                <div className="flex items-center gap-1 text-xs text-gray-600 mb-2">
                  <User size={12} />
                  <span>{item.collaborator.firstName} {item.collaborator.lastName}</span>
                </div>
              )}

              {/* Options */}
              {item.options.length > 0 && (
                <div className="mt-2 space-y-1">
                  {item.options.map((option, optIndex) => {
                    const optionPrice = typeof option.price === 'number' ?
                      option.price :
                      parseFloat(option.price as any) || 0;
                    return (
                      <div
                        key={optIndex}
                        className="flex items-center justify-between text-xs text-gray-600"
                      >
                        <span>+ {option.name}</span>
                        <span className="font-medium">{optionPrice.toFixed(2)}€</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Durée et prix */}
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-primary/20">
                <div className="flex items-center gap-1 text-xs text-gray-600">
                  <Clock size={12} />
                  <span>{calculateItemDuration(item)} min</span>
                </div>
                <div className="text-sm font-bold text-primary">
                  {calculateItemPrice(item).toFixed(2)}€
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

export default MultiServiceCart;
